import { ComponentType } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import CrossIcon from "@atlaskit/icon/glyph/cross";
import { media } from "@atlaskit/primitives";

import { useHeroState } from "../util/hero-context";

export type ProductModalCardType = {
    readonly eyebrow: string;
    readonly title: string;
    readonly description: string;
    readonly image: string;
    readonly iconTitle?: string;
    readonly iconCollection: {
        readonly icon: React.ReactNode;
        readonly description: string;
    }[];
    readonly BackgroundImage: ComponentType;
    readonly ctaLink: string;
    readonly label: string;
};

const modalStyles = css({
    display: "flex",
    borderRadius: "22px",
    background: "var(--Neutral-Light-200, #F0F1F2)",
    boxShadow:
        "0px 0px 8px 0px rgba(9, 30, 66, 0.16), 0px 0px 1px 0px rgba(9, 30, 66, 0.12)",
    justifyContent: "flex-end",
    height: "80vh",
    [media.only.md]: {
        width: "80%",
        height: "80%",
        flexDirection: "column-reverse",
        backgroundColor: "#FFF"
    },
    [media.only.sm]: {
        width: "80%",
        height: "80%",
        flexDirection: "column-reverse",
        backgroundColor: "#FFF"
    },
    [media.only.xs]: {
        width: "80%",
        height: "80%",
        flexDirection: "column-reverse",
        backgroundColor: "#FFF"
    },
    [media.below.xs]: {
        height: "90%",
        width: "80%",
        flexDirection: "column-reverse",
        backgroundColor: "#FFF"
    }
});

const modalImageStyles = css({
    flexGrow: "1",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center"
});

const imageStyles = css({
    maxHeight: "80%",
    maxWidth: "75%",
    position: "absolute",
    [media.below.sm]: {
        maxWidth: "80%",
        maxHeight: "80%"
    }
});

const backgroundStyles = css({
    height: "90%",
    width: "90%",
    position: "absolute",
    display: "flex",
    justifyContent: "center",
    [media.below.sm]: {
        width: "90%",
        height: "90%"
    }
});

const modalContentStyles = css({
    width: "367px",
    borderRadius: "22px",
    background: "#FFF",
    [media.below.lg]: {
        width: "100%"
    }
});

const textSpaceStyles = css({
    padding: "0px 31px"
});

const closeModalButtonStyles = css({
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    padding: "0",
    backgroundColor: "transparent",
    border: "none",
    marginBlockStart: "16px",
    cursor: "pointer"
});

const eyebrowStyles = css({
    color: "var(--Primary-Black, var(--DarkNeutral-Opaque-DarkNeutral-100, #101214))",
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: "var(--atlassian-recommended-system-fonts)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "18px",
    letterSpacing: "1px",
    textTransform: "uppercase",
    paddingBlockStart: "11px",
    [media.below.sm]: {
        fontSize: "12px",
        lineHeight: "16px"
    }
});

const titleStyles = css({
    color: "var(--Primary-Black, var(--DarkNeutral-Opaque-DarkNeutral-100, #101214))",
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: "var(--atlassian-recommended-system-fonts)",
    fontSize: "32px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "38px",
    marginBlockStart: "5px",
    [media.below.sm]: {
        fontSize: "24px",
        lineHeight: "28.8px"
    }
});

const descriptionStyles = css({
    marginBlockStart: "23px",
    [media.below.sm]: {
        fontSize: "16px",
        lineHeight: "24px",
        marginBlockEnd: "16px"
    }
});

const seporatorStyles = css({
    height: "1px",
    background: "#96999E",
    marginBlockStart: "32px"
});

const iconTitleStyles = css({
    marginBlockStart: "32px",
    marginBlockEnd: "9px",
    color: "#000",
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "24px",
    width: "241px"
});

const iconDescriptionStyles = css({
    color: "#000",
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "20px"
});

const iconContainerStyles = css({
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "8px",
    [media.below.sm]: {
        gap: "4px"
    }
});

const iconAndTextStyles = css({
    display: "flex"
});

const iconStyles = css({
    width: "24px",
    height: "24px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const buttonSpaceStyles = css({
    marginBlockStart: "32px",
    [media.below.sm]: {
        marginBlockStart: "16px",
        marginBlockEnd: "16px"
    }
});

const displayNoneBelowLg = css({
    [media.below.lg]: {
        display: "none"
    }
});

export const ProductModalCard = ({
    eyebrow,
    title,
    description,
    iconCollection,
    iconTitle = "Collaborate with these features",
    image,
    BackgroundImage,
    ctaLink,
    label
}: ProductModalCardType) => {
    const { closeModal } = useHeroState();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleClick = () => {
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            attributes: {
                name: `atlas-55799-${label}-modal-cta-button-clicked`,
                section: "hero-section",
                ctaLink
            }
        }).fire();
    };

    return (
        <div css={modalStyles}>
            <div css={modalImageStyles}>
                <div css={backgroundStyles}>
                    <BackgroundImage />
                </div>
                <img src={image} css={imageStyles} alt="" />
            </div>
            <div css={modalContentStyles}>
                <div css={textSpaceStyles}>
                    <button onClick={closeModal} css={closeModalButtonStyles}>
                        <CrossIcon size="medium" label="Close" />
                    </button>
                    <div css={eyebrowStyles}>{eyebrow}</div>
                    <div css={titleStyles}>{title}</div>
                    <div css={descriptionStyles}>{description}</div>
                    <div css={[seporatorStyles, displayNoneBelowLg]} />
                    <div css={[iconTitleStyles, displayNoneBelowLg]}>
                        {iconTitle}
                    </div>
                    <div css={iconContainerStyles}>
                        {iconCollection.map((icon, index) => (
                            <div css={iconAndTextStyles} key={index}>
                                <div css={iconStyles}>{icon.icon}</div>
                                <div css={iconDescriptionStyles}>
                                    {icon.description}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div css={buttonSpaceStyles}>
                        <Button
                            appearance="primary"
                            href={ctaLink}
                            onClick={handleClick}
                        >
                            Get started
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
